import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LAFT_FORM_CONSENTS } from 'src/app/shared/constants/laft-form-consents';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';

@Component({
  selector: 'app-laft-investigation-form',
  templateUrl: './laft-investigation-form.component.html',
  styleUrls: ['./laft-investigation-form.component.scss'],
})
export class LaftInvestigationFormComponent implements OnInit {
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();
  @Input() initData: any;

  /**
   * True while investigaciones is the form's last step. The consents and the
   * submit belong to whichever step is last, so with the early-deposit flag on
   * they move to the ID-copies step and this becomes an ordinary step.
   */
  @Input() isLastStep = true;

  formSubmitSubject$ = new Subject();
  public investigationDataForm: FormGroup;
  public consents = LAFT_FORM_CONSENTS;

  public caseStatusOptions = [
    { value: 'Investigación en fiscalía', text: 'Investigación en fiscalía' },
    { value: 'Acusación juicio oral o condena', text: 'Acusación juicio oral o condena' },
  ];

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.investigationDataForm = this.formBuilder.group({
      isInvestigated: [false, [Validators.required]],
      prosecutorOffice: ['', []],
      caseStatus: ['', []],
      caseNumber: ['', []],
      defendantName: ['', []],
    });

    // Added rather than merely hidden: a requiredTrue control that is never
    // rendered would hold this step invalid forever, and a control left behind
    // would keep emitting acceptsContract:false over step 5's answer.
    if (this.isLastStep) {
      this.investigationDataForm.addControl('acceptsContract', new FormControl(false, [Validators.requiredTrue]));
      this.investigationDataForm.addControl('acceptsCommercialUse', new FormControl(false));
    }

    this.investigationDataForm.get('isInvestigated')?.valueChanges.subscribe((isInvestigated: boolean) => {
      const fields = ['prosecutorOffice', 'caseStatus', 'caseNumber', 'defendantName'];

      fields.forEach(field => {
        const control = this.investigationDataForm.get(field);
        if (isInvestigated) {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
          control?.setValue('');
        }
        control?.updateValueAndValidity();
      });
    });

    this.investigationDataForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      const cleanedData = this.removeEmptyFields(this.investigationDataForm.value);
      this.formChangesEvent.emit(cleanedData);
    });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.investigationDataForm.markAsDirty();
          this.investigationDataForm.markAllAsTouched();
          Object.keys(this.investigationDataForm.controls).forEach(key => {
            const control = this.investigationDataForm.get(key);
            control?.updateValueAndValidity({ emitEvent: true });
          });
        }),
        filter(() => this.investigationDataForm.valid)
      )
      .subscribe(() => {
        const cleanedData = this.removeEmptyFields(this.investigationDataForm.value);
        this.nextStep.next(cleanedData);
      });

    this.populateFormData();
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  private populateFormData(): void {
    this.investigationDataForm.patchValue({
      isInvestigated: this.initData?.isInvestigated ?? false,
      acceptsContract: this.initData?.acceptsContract ?? false,
      prosecutorOffice: this.initData?.prosecutorOffice || '',
      caseStatus: this.initData?.caseStatus || '',
      caseNumber: this.initData?.caseNumber || '',
      defendantName: this.initData?.defendantName || '',
      acceptsCommercialUse: this.initData?.acceptsCommercialUse ?? false,
    });
  }

  removeEmptyFields(data: any): any {
    const cleaned = { ...data };

    if (!data.isInvestigated) {
      cleaned.prosecutorOffice = '';
      cleaned.caseStatus = '';
      cleaned.caseNumber = '';
      cleaned.defendantName = '';
    }

    return cleaned;
  }
}
